import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Search, Check } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Building2, UserCircle } from 'lucide-react'
import { useOrganizations, useCurrentOrganization } from '../../../../hooks/queries'
import { useAuthStore } from '../../../../store/authStore'
import { queryClient } from '../../../../lib/react-query/queryClient'
import { queryKeys } from '../../../../lib/react-query'
import { useIntl } from 'react-intl'

function FastSwitchOrg({ btnClass }: { btnClass: string }) {
    const intl = useIntl()
    const dataOrgList = useAuthStore((state) => state.dataOrgList)
    const navigate = useNavigate()
    const orgId = useAuthStore((state) => state.orgId)
    const [searchParams] = useSearchParams()
    const isActive = useMemo(() => !!searchParams.get('id'), [searchParams])

    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const containerRef = useRef<HTMLDivElement>(null)

    const { refetch: refetchOrganizations } = useOrganizations()
    const { refetch: refetchCurrentOrg } = useCurrentOrganization()

    const fetchOrganizations = async () => {
        try {
            await refetchOrganizations()
            try { await refetchCurrentOrg() } catch { }
        } catch { }
    }

    useEffect(() => { fetchOrganizations() }, [])

    useEffect(() => {
        if (orgId) {
            const name = dataOrgList?.find((i) => i.id === orgId)?.name || ''
            useAuthStore.getState().SetOrgName(name)
        }
    }, [orgId])

    // Close on outside click
    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
                setSearch('')
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const currentOrg = dataOrgList?.find((o) => o.id === orgId)
    const filtered = useMemo(
        () => (dataOrgList ?? []).filter((o) => o.name.toLowerCase().includes(search.toLowerCase())),
        [dataOrgList, search]
    )

    const handleSelect = (id: string) => {
        useAuthStore.getState().SetOrgId(id)
        queryClient.invalidateQueries({ queryKey: queryKeys.user.permissions })
        queryClient.invalidateQueries({ queryKey: queryKeys.user.profile })
        setOpen(false)
        setSearch('')
    }

    return (
        <div className='d-flex align-items-center gap-1' ref={containerRef} style={{ position: 'relative' }}>
            {/* Trigger button */}
            <button
                type='button'
                disabled={isActive}
                onClick={() => { if (!isActive) setOpen((v) => !v) }}
                className='d-flex align-items-center gap-2 px-3 border-0 header-icon-trigger variant-secondary btn'
                style={{
                    width: 'auto',
                    height: 40,
                    maxWidth: 200,
                    outline: 'none',
                    boxShadow: 'none',
                }}
            >
                <Building2 size={20} className='text-primary' />
                <span
                    className='text-truncate fw-semibold'
                    style={{ fontSize: '1.05rem', maxWidth: 130 }}
                >
                    {currentOrg?.name ?? '—'}
                </span>
                {open ? <ChevronUp size={16} className='text-muted' /> : <ChevronDown size={16} className='text-muted' />}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div
                    className='shadow py-2 border border-light-subtle card'
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        minWidth: 240,
                        zIndex: 1050,
                        borderRadius: 10,
                    }}
                >
                    {/* Search */}
                    {(dataOrgList?.length ?? 0) > 5 && (
                        <div className='px-3 pb-2'>
                            <div className='input-group input-group-sm' style={{ direction: "ltr" }}>
                                <span className='input-group-text bg-light border-0 ps-2'>
                                    <Search size={16} className='text-muted' />
                                </span>
                                <input
                                    autoFocus
                                    type='text'
                                    className='bg-light border-0 form-control form-control-sm'
                                    placeholder={intl.formatMessage({ id: 'Search' })}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ boxShadow: 'none', outline: 'none' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* List */}
                    <ul
                        className='my-2 px-2 overflow-auto list-unstyled'
                        style={{ maxHeight: 260 }}
                    >
                        {filtered.length === 0 ? (
                            <li className='py-3 text-center text-muted' style={{ fontSize: '0.8rem' }}>
                                No results
                            </li>
                        ) : filtered.map((org) => {
                            const isSelected = org.id === orgId
                            return (
                                <li key={org.id}>
                                    <button
                                        type='button'
                                        onClick={() => handleSelect(org.id)}
                                        className={`btn mt-2 w-100 text-start d-flex align-items-center gap-2 px-3 py-2 rounded-2 ${isSelected ? 'btn-light-primary' : 'btn-light'}`}
                                        style={{ fontSize: '0.82rem' }}
                                    >
                                        <Building2 size={18} className={isSelected ? 'text-primary' : 'text-muted'} />
                                        <span className='flex-grow-1 text-truncate fs-7'>{org.name}</span>
                                        {isSelected && (
                                            <Check size={16} className='text-primary ms-auto' />
                                        )}
                                    </button>
                                </li>
                            )
                        })}
                    </ul>

                    {/* Footer */}
                    <div className='mt-2 px-3 pt-2 border-top'>
                        <button
                            type='button'
                            onClick={() => { navigate('/select-organization'); setOpen(false) }}
                            className='d-flex justify-content-center align-items-center gap-2 w-100 btn btn-sm btn-light-primary'
                            style={{ fontSize: '1rem' }}
                        >
                            <UserCircle size={20} />
                            {intl.formatMessage({ id: "Manage Organizations" })}

                        </button>
                    </div>
                </div>
            )}

        </div>
    )
}

export default FastSwitchOrg
